import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import API from "../../utils/api";
import PipelineFunnel from "../../components/PipelineFunnel";
import AnalyticsDashboard from "../../components/AnalyticsDashboard";
import ReportsExport from "../../components/ReportsExport";
import FieldDayMap from "../../components/FieldDayMap";
import CommandCenter from "../../components/CommandCenter";

const CO_CFG = {
  ebek:    { label: "Ebek",     accent: "#E31E24", icon: "" },
  ebekmed: { label: "Ebek Med", accent: "#2AB5A5", icon: "" },
};

export default function ExecutiveDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary]           = useState([]);
  const [combined, setCombined]         = useState(null);
  const [followupStats, setFollowupStats] = useState([]);
  const [flaggedActions, setFlaggedActions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [tab, setTab]                   = useState("overview");
  const [period, setPeriod]             = useState("30");
  const [fieldDate, setFieldDate]       = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }));
  const [fieldCheckins, setFieldCheckins] = useState([]);
  const [fieldLoading, setFieldLoading]   = useState(false);
  const [fieldMapUser, setFieldMapUser]   = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, perfRes, fuStatsRes, flaggedRes] = await Promise.all([
        API.get("/reports/company-summary"),
        API.get(`/reports/performance?days=${period}`),
        API.get("/followups/stats"),
        API.get("/follow-up-actions/flagged"),
      ]);
      setSummary(sumRes.data);
      setFollowupStats(fuStatsRes.data);
      setFlaggedActions(flaggedRes.data || []);

      // Aggregate combined numbers
      const perf = perfRes.data;
      const totalVisits  = perf.reduce((s, p) => s + (p.total_visits||0), 0);
      const totalHot     = perf.reduce((s, p) => s + (p.hot_leads||0), 0);
      const totalWarm    = perf.reduce((s, p) => s + (p.warm_leads||0), 0);
      const totalCold    = perf.reduce((s, p) => s + (p.cold_leads||0), 0);
      const activeReps   = perf.filter(p => p.total_visits > 0).length;
      setCombined({ totalVisits, totalHot, totalWarm, totalCold, activeReps, reps: perf });
    } catch { toast.error("Failed to load executive summary"); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetch(); }, [fetch]);

  const todayDate = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const NAV_ITEMS = [
    { key: "overview",    label: "Home",        icon: "⌂" },
    { key: "field",       label: "Field Day",   icon: "📍" },
    { key: "companies",   label: "By Company",  icon: "🏢" },
    { key: "pipeline",    label: "Pipeline",    icon: "🔵" },
    { key: "followups",   label: "Follow-ups",  icon: "📞" },
    { key: "leaderboard", label: "Leaderboard", icon: "🏆" },
    { key: "flagged",     label: "Flagged",     icon: "🚩", badge: flaggedActions.length },
    { key: "analytics",   label: "Analytics",   icon: "📊" },
    { key: "reports",     label: "Reports",     icon: "📋" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Inter',sans-serif" }}>

      {/* ── LEFT SIDEBAR ─────────────────────────────────────────── */}
      <nav style={{ width: 220, minWidth: 220, background: "linear-gradient(180deg,#0f172a 0%,#1e293b 100%)", display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh", overflowY: "auto", zIndex: 40, boxShadow: "2px 0 16px rgba(0,0,0,.18)" }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "22px 16px 18px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#7C3AED,#D97706)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📊</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc", whiteSpace: "nowrap" }}>Sales CRM</div>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 1 }}>Executive View</div>
          </div>
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, padding: "14px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV_ITEMS.map(({ key, label, icon, badge }) => (
            <button key={key}
              onClick={() => {
                setTab(key);
                if (key === "field") {
                  setFieldLoading(true);
                  const d = fieldDate;
                  API.get(`/checkins/report?from=${d}&to=${d}`)
                    .then(r => setFieldCheckins((r.data || []).filter(c => c.plan_type === "institution_visit")))
                    .catch(() => {})
                    .finally(() => setFieldLoading(false));
                }
              }}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 14px", border: "none", borderRadius: 10,
                background: tab === key ? "rgba(124,58,237,.2)" : "transparent",
                boxShadow: tab === key ? "inset 3px 0 0 #7C3AED" : "none",
                color: tab === key ? "#c4b5fd" : "#94a3b8",
                fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                transition: "background .15s, color .15s" }}
              onMouseEnter={e => { if (tab !== key) { e.currentTarget.style.background = "rgba(255,255,255,.06)"; e.currentTarget.style.color = "#e2e8f0"; } }}
              onMouseLeave={e => { if (tab !== key) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; } }}
            >
              <span style={{ fontSize: 17, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
              <span style={{ flex: 1 }}>{label}</span>
              {badge > 0 && (
                <span style={{ background: "#ef4444", color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Footer: date + sign out */}
        <div style={{ padding: "14px 16px", borderTop: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ fontSize: 11, color: "#475569", marginBottom: 10, lineHeight: 1.4 }}>📅 {todayDate}</div>
          <button onClick={() => { logout(); navigate("/login"); }}
            style={{ width: "100%", background: "rgba(239,68,68,.15)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 700, color: "#fca5a5", cursor: "pointer", fontFamily: "inherit" }}>
            Sign Out
          </button>
        </div>
      </nav>

      {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
      <div style={{ padding: "20px 24px", maxWidth: 1100, margin: "0 auto" }}>
        {/* Period selector */}
        {tab !== "overview" && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {[["7","Last 7 Days"],["30","Last 30 Days"],["90","Last 90 Days"]].map(([v,l]) => (
            <button key={v} onClick={() => setPeriod(v)}
              style={{ padding: "8px 16px", borderRadius: 100, border: "none", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer",
                background: period===v ? "#7C3AED" : "#f1f5f9",
                color: period===v ? "#fff" : "#64748b" }}>{l}</button>
          ))}
        </div>
        )}

        {loading && tab !== "field" && tab !== "overview" && <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>Loading</div>}

        {/* FIELD DAY TAB */}
        {tab === "field" && (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"12px 16px" }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#475569" }}>Date:</span>
              <input
                type="date"
                value={fieldDate}
                onChange={e => {
                  const d = e.target.value;
                  setFieldDate(d);
                  setFieldLoading(true);
                  API.get(`/checkins/report?from=${d}&to=${d}`)
                    .then(r => setFieldCheckins((r.data || []).filter(c => c.plan_type === "institution_visit")))
                    .catch(() => {})
                    .finally(() => setFieldLoading(false));
                }}
                style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 10px", fontSize:13, fontFamily:"inherit" }}
              />
              <span style={{ fontSize:12, color:"#94a3b8" }}>{fieldCheckins.length} rep{fieldCheckins.length !== 1 ? "s" : ""} on field visit</span>
            </div>

            {fieldLoading && <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>Loading...</div>}

            {!fieldLoading && fieldCheckins.length === 0 && (
              <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:60, textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
                <div style={{ fontWeight:700, color:"#1e293b", marginBottom:4 }}>No field visits on this day</div>
                <div style={{ fontSize:13, color:"#94a3b8" }}>Reps checked in as "Institution Visit" will appear here</div>
              </div>
            )}

            {!fieldLoading && fieldCheckins.map(c => {
              const co = CO_CFG[c.user_company || c.company] || CO_CFG.ebek;
              return (
                <div
                  key={c.user_id || c.id}
                  style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:14, cursor:"pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                  onClick={() => setFieldMapUser({ id: c.user_id, name: c.name, date: fieldDate, accent: co.accent })}
                >
                  <div style={{ width:44, height:44, borderRadius:12, background:co.accent+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:co.accent, flexShrink:0 }}>
                    {(c.name||"?")[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"#1e293b" }}>{c.name}</div>
                    <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
                      <span style={{ background:co.accent+"15", color:co.accent, borderRadius:100, padding:"1px 8px", fontWeight:700, marginRight:6 }}>{co.label}</span>
                      {c.check_in_time ? `In ${new Date(c.check_in_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}` : ""}
                      {c.check_out_time ? ` · Out ${new Date(c.check_out_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}` : " · Active"}
                      {c.total_km_travelled ? ` · ${Number(c.total_km_travelled).toFixed(1)} km` : ""}
                    </div>
                  </div>
                  <div style={{ background:"#7C3AED15", color:"#7C3AED", borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:700, flexShrink:0 }}>
                    📍 View Map →
                  </div>
                </div>
              );
            })}
          </>
        )}

        {tab === "overview" && (
          <CommandCenter
            accent="#7C3AED"
            onOpenTab={(k) => {
              const mapped = k === "leads" ? "pipeline" : k === "briefing" ? "field" : k;
              setTab(mapped);
              if (mapped === "field") {
                setFieldLoading(true);
                const d = fieldDate;
                API.get(`/checkins/report?from=${d}&to=${d}`)
                  .then(r => setFieldCheckins((r.data || []).filter(c => c.plan_type === "institution_visit")))
                  .catch(() => {})
                  .finally(() => setFieldLoading(false));
              }
            }}
            onOpenPerson={(person, day) => setFieldMapUser({ id: person.user_id, name: person.name, date: day })}
            drillItems={[
              ["field", "Visits & maps"],
              ["pipeline", "Pipeline"],
              ["followups", "Follow-ups"],
              ["reports", "Exports"],
            ]}
          />
        )}

        {/* BY COMPANY TAB */}
        {!loading && tab === "companies" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {summary.map(co => {
              const cfg = CO_CFG[co.company] || CO_CFG.ebek;
              const coReps = combined?.reps?.filter(r => r.company === co.company) || [];
              const totalVisits = coReps.reduce((s, r) => s + (r.total_visits||0), 0);
              return (
                <div key={co.company} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
                  {/* Company header */}
                  <div style={{ background: cfg.accent, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 28 }}>{cfg.icon}</div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{cfg.label}</div>
                      <div style={{ fontSize: 12, color: "#ffffff99" }}>{co.total_employees} staff  {co.checked_in_today} in today</div>
                    </div>
                  </div>
                  {/* Stats grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0 }}>
                    {[
                      { label: "Visits Today", value: co.visits_today || 0 },
                      { label: `Visits (${period}d)`, value: totalVisits },
                      { label: "Hot Leads", value: co.hot_leads_total || 0 },
                      { label: "Active Reps", value: coReps.filter(r=>r.total_visits>0).length },
                    ].map((s, i) => (
                      <div key={s.label} style={{ padding: "16px 12px", textAlign: "center", borderRight: i < 3 ? "1px solid #f1f5f9" : "none" }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: cfg.accent }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Top 3 reps */}
                  {coReps.length > 0 && (
                    <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", marginBottom: 10, textTransform: "uppercase" }}>Top Performers</div>
                      {coReps.slice(0, 3).map((r, i) => (
                        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: i===0?cfg.accent:"#94a3b8", width: 20 }}>#{i+1}</div>
                          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{r.name}</div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: cfg.accent }}>{r.total_visits} visits</div>
                          <div style={{ fontSize: 11, color: "#dc2626" }}>{r.hot_leads}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* LEADERBOARD TAB */}
        {!loading && tab === "leaderboard" && combined && (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", fontSize: 14, fontWeight: 700 }}> All-Company Leaderboard  Last {period} Days</div>

            {/* Podium for top 3 */}
            {combined.reps.length >= 3 && (
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 16, padding: "24px 20px 0", background: "linear-gradient(to bottom,#fafafa,#fff)" }}>
                {[combined.reps[1], combined.reps[0], combined.reps[2]].map((r, pos) => {
                  const heights = [100, 130, 85];
                  const medals  = ["","",""];
                  const bgColors = ["#e2e8f0","linear-gradient(135deg,#D97706,#fbbf24)","#f1f5f9"];
                  const cfg = CO_CFG[r.company] || CO_CFG.ebek;
                  return (
                    <div key={r.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: 24 }}>{medals[pos]}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", textAlign: "center", maxWidth: 80 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{cfg.label}</div>
                      <div style={{ width: 70, height: heights[pos], background: pos===1 ? "linear-gradient(180deg,#7C3AED,#4f46e5)" : "#e2e8f0", borderRadius: "10px 10px 0 0", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: pos===1?"#fff":cfg.accent }}>{r.total_visits}</div>
                        <div style={{ fontSize: 10, color: pos===1?"#c4b5fd":"#94a3b8" }}>visits</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Full list */}
            <div style={{ padding: "0 0 8px" }}>
              {combined.reps.map((r, i) => {
                const cfg = CO_CFG[r.company] || CO_CFG.ebek;
                const maxV = combined.reps[0]?.total_visits || 1;
                return (
                  <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid #f8fafc" }}>
                    <div style={{ width: 28, fontWeight: 800, fontSize: 13, color: i<3?"#7C3AED":"#94a3b8", textAlign: "center" }}>
                      {i<3 ? ["","",""][i] : `#${i+1}`}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{r.name}</span>
                          <span style={{ marginLeft: 8, fontSize: 11, background: cfg.accent+"20", color: cfg.accent, borderRadius: 4, padding: "1px 6px", fontWeight: 700 }}>{cfg.label}</span>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 900, color: cfg.accent }}>{r.total_visits}</span>
                      </div>
                      <div style={{ height: 4, background: "#f1f5f9", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(r.total_visits/maxV)*100}%`, background: cfg.accent, borderRadius: 2 }} />
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 4, fontSize: 11, color: "#64748b" }}>
                        <span>{r.days_active} days</span>
                        <span>{r.hot_leads}</span>
                        <span>{r.warm_leads}</span>
                        <span>{r.cold_leads}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {combined.reps.length === 0 && <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>No data for this period</div>}
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {tab === "analytics" && (
          <AnalyticsDashboard accent="#7C3AED" userRole={user?.role} userCompany={user?.company} />
        )}

        {/* REPORTS TAB */}
        {tab === "reports" && (
          <ReportsExport accent="#7C3AED" userRole={user?.role} userCompany={user?.company} />
        )}

        {/* FLAGGED TAB */}
        {tab === "flagged" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>🚩 Flagged Location Mismatches</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                Employees who logged a Direct Meeting but were &gt;500m from the institution — visible to managers/admins/executive only
              </div>
            </div>
            {flaggedActions.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <div style={{ fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>No flagged actions</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>All direct meets have been within the expected location range</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {flaggedActions.map(a => (
                  <div key={a.id} style={{ background: "#fff", border: "1.5px solid #fecaca", borderRadius: 14, padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>{a.institution_name}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                          👤 {a.employee_name} — {a.employee_email}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                          {new Date(a.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ padding: "4px 12px", background: "#fef2f2", borderRadius: 20, color: "#dc2626", fontWeight: 800, fontSize: 13 }}>
                          📍 {a.location_mismatch_meters}m away
                        </div>
                      </div>
                    </div>
                    {a.manager_flag_note && (
                      <div style={{ background: "#fef2f2", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#9a3412", lineHeight: 1.5 }}>
                        🔍 {a.manager_flag_note}
                      </div>
                    )}
                    {a.notes && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", background: "#f8fafc", borderRadius: 8, padding: "6px 10px" }}>
                        📝 {a.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* PIPELINE TAB */}
        {tab === "pipeline" && <PipelineFunnel accent="#7C3AED" />}

        {/* FOLLOW-UPS TAB */}
        {!loading && tab === "followups" && (
          <>
            {/* Org-wide summary */}
            <div style={{ background: "linear-gradient(135deg,#1e1b4b,#312e81)", borderRadius: 20, padding: "24px", marginBottom: 20, color: "#fff" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#a5b4fc", marginBottom: 16, textTransform: "uppercase", letterSpacing: 1 }}>Organisation Follow-up Summary</div>
              <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                {[
                  { label: "Total",   value: followupStats.reduce((a,r)=>a+parseInt(r.total_followups||0),0), color: "#c7d2fe" },
                  { label: "Done",    value: followupStats.reduce((a,r)=>a+parseInt(r.completed||0),0),        color: "#6ee7b7" },
                  { label: "Missed",  value: followupStats.reduce((a,r)=>a+parseInt(r.missed||0),0),           color: "#fca5a5" },
                  { label: "Overdue", value: followupStats.reduce((a,r)=>a+parseInt(r.overdue||0),0),          color: "#fde68a" },
                ].map(s => {
                  const total = followupStats.reduce((a,r)=>a+parseInt(r.total_followups||0),0);
                  return (
                    <div key={s.label}>
                      <div style={{ fontSize: 36, fontWeight: 900, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 12, color: "#a5b4fc" }}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
              {(() => {
                const total = followupStats.reduce((a,r)=>a+parseInt(r.total_followups||0),0);
                const done  = followupStats.reduce((a,r)=>a+parseInt(r.completed||0),0);
                const pct   = total > 0 ? Math.round(done/total*100) : 0;
                return (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                      <span style={{ fontSize:12, color:"#a5b4fc" }}>Completion Rate</span>
                      <span style={{ fontSize:14, fontWeight:800, color: pct>=70?"#6ee7b7": pct>=40?"#fde68a":"#fca5a5" }}>{pct}%</span>
                    </div>
                    <div style={{ height:6, background:"#ffffff20", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background: pct>=70?"#6ee7b7": pct>=40?"#fde68a":"#fca5a5", borderRadius:3 }} />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Per-company summary */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
              {["ebek","ebekmed"].map(co => {
                const cfg = CO_CFG[co] || CO_CFG.ebek;
                const coStats = followupStats.filter(r => r.rep_company === co);
                const coTotal = coStats.reduce((a,r)=>a+parseInt(r.total_followups||0),0);
                const coDone  = coStats.reduce((a,r)=>a+parseInt(r.completed||0),0);
                const coMiss  = coStats.reduce((a,r)=>a+parseInt(r.missed||0),0);
                const coOver  = coStats.reduce((a,r)=>a+parseInt(r.overdue||0),0);
                const pct     = coTotal > 0 ? Math.round(coDone/coTotal*100) : 0;
                return (
                  <div key={co} style={{ background:"#fff", border:`1.5px solid ${cfg.accent}30`, borderRadius:16, padding:16 }}>
                    <div style={{ fontSize:14, fontWeight:800, color:cfg.accent, marginBottom:12 }}>{cfg.icon} {cfg.label}</div>
                    {[
                      { label:"Total", value:coTotal },
                      { label:"Done",  value:coDone  },
                      { label:"Missed",value:coMiss  },
                      { label:"Overdue",value:coOver },
                    ].map(s => (
                      <div key={s.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12 }}>
                        <span style={{ color:"#64748b" }}>{s.label}</span>
                        <span style={{ fontWeight:800, color:cfg.accent }}>{s.value}</span>
                      </div>
                    ))}
                    <div style={{ marginTop:10, height:4, background:"#f1f5f9", borderRadius:2 }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:cfg.accent, borderRadius:2 }} />
                    </div>
                    <div style={{ fontSize:11, color:cfg.accent, fontWeight:700, marginTop:4 }}>{pct}% completion</div>
                  </div>
                );
              })}
            </div>

            {/* Full rep-level table */}
            <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden" }}>
              <div style={{ padding:"14px 20px", borderBottom:"1px solid #f1f5f9", fontSize:14, fontWeight:700 }}> Rep-level Follow-up Performance</div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr", padding:"10px 20px", background:"#f8fafc", fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase" }}>
                <span>Rep</span><span style={{textAlign:"center"}}>Total</span><span style={{textAlign:"center"}}>Done</span><span style={{textAlign:"center"}}>Missed</span><span style={{textAlign:"center"}}>Overdue</span><span style={{textAlign:"center"}}>Rate</span>
              </div>
              {followupStats.length === 0 ? (
                <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>No follow-up data yet</div>
              ) : followupStats.map(r => {
                const total = parseInt(r.total_followups || 0);
                const done  = parseInt(r.completed || 0);
                const miss  = parseInt(r.missed    || 0);
                const over  = parseInt(r.overdue   || 0);
                const pct   = total > 0 ? Math.round(done/total*100) : 0;
                const cfg   = CO_CFG[r.rep_company] || CO_CFG.ebek;
                return (
                  <div key={r.user_id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr", padding:"12px 20px", borderBottom:"1px solid #f8fafc", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{r.rep_name}</div>
                      <span style={{ fontSize:10, background:cfg.accent+"20", color:cfg.accent, borderRadius:4, padding:"1px 6px", fontWeight:700 }}>{cfg.label}</span>
                    </div>
                    <div style={{ textAlign:"center", fontSize:15, fontWeight:800, color:"#6366f1" }}>{total}</div>
                    <div style={{ textAlign:"center", fontSize:15, fontWeight:800, color:"#16a34a" }}>{done}</div>
                    <div style={{ textAlign:"center", fontSize:15, fontWeight:800, color:miss>0?"#dc2626":"#94a3b8" }}>{miss}</div>
                    <div style={{ textAlign:"center", fontSize:15, fontWeight:800, color:over>0?"#ea580c":"#94a3b8" }}>{over}</div>
                    <div style={{ textAlign:"center" }}>
                      <span style={{ padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:700,
                        background: pct>=70?"#f0fdf4": pct>=40?"#fff7ed":"#fef2f2",
                        color:      pct>=70?"#16a34a": pct>=40?"#ea580c":"#dc2626" }}>
                        {pct}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      {fieldMapUser && (
        <FieldDayMap
          userId={fieldMapUser.id}
          userName={fieldMapUser.name}
          date={fieldMapUser.date || fieldDate}
          accent={fieldMapUser.accent || "#7C3AED"}
          onClose={() => setFieldMapUser(null)}
        />
      )}
      </div>
    </div>
  );
}
